import { useEffect, useImperativeHandle } from "react";
import { useStopwatch } from "react-timer-hook";
import { TimerComponent } from "@/components/TimerComponent";
import { serverNow } from "@/utils/serverClock";
import type { IAttendanceActivity } from "@/types/(my-space)/timeAttendace.interface";

type IProps = {
  ref: React.RefObject<{ pause: () => void } | null>;
  isCurrentlyCheckedIn: boolean;
  attendanceActivities: IAttendanceActivity[];
};

const TimeCounter = ({
  ref,
  isCurrentlyCheckedIn,
  attendanceActivities,
}: IProps) => {
  const { seconds, minutes, hours, reset, pause } = useStopwatch({
    autoStart: false,
  });

  useImperativeHandle(ref, () => ({
    pause,
  }));

  useEffect(() => {
    const firstCheckInTime = attendanceActivities?.[0]?.clockInTime;
    if (!firstCheckInTime) return;

    const elapsedSeconds = attendanceActivities.reduce((acc, activity) => {
      if (activity.clockOutTime) {
        const diff =
          new Date(activity.clockOutTime).getTime() -
          new Date(activity.clockInTime).getTime();
        if (diff > 0) {
          return acc + diff / 1000;
        }
      }
      return (
        acc + (serverNow() - new Date(activity.clockInTime).getTime()) / 1000
      );
    }, 0);

    // Local clock on purpose: react-timer-hook ticks against Date.now().
    const offsetTimestamp = new Date();
    offsetTimestamp.setSeconds(offsetTimestamp.getSeconds() + elapsedSeconds);
    reset(offsetTimestamp, isCurrentlyCheckedIn);
  }, [attendanceActivities, isCurrentlyCheckedIn, reset]);

  return (
    <div className="flex items-center gap-2 ms-2">
      <TimerComponent type="HH" value={hours} />
      <TimerComponent type="MM" value={minutes} />
      <TimerComponent type="SS" value={seconds} />
    </div>
  );
};

export { TimeCounter };
